import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';

export const getCustomHeatMapStats = createAsyncThunk(
  'heatMapStats/getCustomHeatMapStats',
  async ({ startDate, endDate, group_id }, { getState, rejectWithValue }) => {
    try {
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;
      const business_id = loginToken?.response?.user?.business_id;

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Karachi';

      let query = `range=custom&timezone=${timezone}&from=${startDate}&to=${endDate}`;
      if (group_id) {
        query += `&group_id=${encodeURIComponent(group_id)}`;
      }

      const response = await axios.get(
        `${ROOT_URL}/analytics/overview/heat-map/${business_id}?${query}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getTodayHeatMapStats = createAsyncThunk(
  'heatMapStats/getTodayHeatMapStats',
  async ({ group_id } = {}, { getState, rejectWithValue }) => {
    try {
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;
      const business_id = loginToken?.response?.user?.business_id;
      
      // Get timezone (you can modify this to get from user settings if needed)
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Karachi';

      let query = `range=today&timezone=${timezone}`;
      if (group_id) {
        query += `&group_id=${encodeURIComponent(group_id)}`;
      }

      const response = await axios.get(
        `${ROOT_URL}/analytics/overview/heat-map/${business_id}?${query}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getYesterdayHeatMapStats = createAsyncThunk(
  'heatMapStats/getYesterdayHeatMapStats',
  async ({ group_id } = {}, { getState, rejectWithValue }) => {
    try {
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;
      const business_id = loginToken?.response?.user?.business_id;
      
      // Get timezone (you can modify this to get from user settings if needed)
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Karachi';

      let query = `range=yesterday&timezone=${timezone}`;
      if (group_id) {
        query += `&group_id=${encodeURIComponent(group_id)}`;
      }

      const response = await axios.get(
        `${ROOT_URL}/analytics/overview/heat-map/${business_id}?${query}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getYearlyHeatMapStats = createAsyncThunk(
  'heatMapStats/getYearlyHeatMapStats',
  async ({ group_id } = {}, { getState, rejectWithValue }) => {
    try {
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;
      const business_id = loginToken?.response?.user?.business_id;
      
      // Get timezone (you can modify this to get from user settings if needed)
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Karachi';

      let query = `range=1y&timezone=${timezone}`;
      if (group_id) {
        query += `&group_id=${encodeURIComponent(group_id)}`;
      }

      const response = await axios.get(
        `${ROOT_URL}/analytics/overview/heat-map/${business_id}?${query}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getWeeklyHeatMapStats = createAsyncThunk(
  'heatMapStats/getWeeklyHeatMapStats',
  async ({ group_id } = {}, { getState, rejectWithValue }) => {
    try {
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;
      const business_id = loginToken?.response?.user?.business_id;
      
      // Get timezone (you can modify this to get from user settings if needed)
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Karachi';

      let query = `range=7d&timezone=${timezone}`;
      if (group_id) {
        query += `&group_id=${encodeURIComponent(group_id)}`;
      }

      const response = await axios.get(
        `${ROOT_URL}/analytics/overview/heat-map/${business_id}?${query}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getMonthlyHeatMapStats = createAsyncThunk(
  'heatMapStats/getMonthlyHeatMapStats',
  async ({ group_id } = {}, { getState, rejectWithValue }) => {
    try {
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;
      const business_id = loginToken?.response?.user?.business_id;
      
      // Get timezone (you can modify this to get from user settings if needed)
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Karachi';

      let query = `range=30d&timezone=${timezone}`;
      if (group_id) {
        query += `&group_id=${encodeURIComponent(group_id)}`;
      }

      const response = await axios.get(
        `${ROOT_URL}/analytics/overview/heat-map/${business_id}?${query}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  todayData: {
    loading: false,
    error: null,
    data: null,
  },
  yesterdayData: {
    loading: false,
    error: null,
    data: null,
  },
  weeklyData: {
    loading: false,
    error: null,
    data: null,
  },
  monthlyData: {
    loading: false,
    error: null,
    data: null,
  },
  yearlyData: {
    loading: false,
    error: null,
    data: null,
  },
  customData: {
    loading: false,
    error: null,
    data: null,
  }
};

const getHeatMapStatsSlice = createSlice({
  name: 'getHeatMapStats',
  initialState,
  reducers: {
    resetHeatMapStatsState: (state) => {
      state.todayData = {
        loading: false,
        error: null,
        data: null,
      };
      state.yesterdayData = {
        loading: false,
        error: null,
        data: null,
      };
      state.weeklyData = {
        loading: false,
        error: null,
        data: null,
      };
      state.monthlyData = {
        loading: false,
        error: null,
        data: null,
      };
      state.yearlyData = {
        loading: false,
        error: null,
        data: null,
      };
      state.customData = {
        loading: false,
        error: null,
        data: null,
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Custom heatmap cases
      .addCase(getCustomHeatMapStats.pending, (state) => {
        state.customData.loading = state.customData.data == null;
        state.customData.error = null;
      })
      .addCase(getCustomHeatMapStats.fulfilled, (state, action) => {
        state.customData.loading = false;
        state.customData.data = action.payload.data;
        state.customData.error = null;
      })
      .addCase(getCustomHeatMapStats.rejected, (state, action) => {
        state.customData.loading = false;
        state.customData.error = action.payload;
        state.customData.data = null;
      })
      // Today heatmap cases
      .addCase(getTodayHeatMapStats.pending, (state) => {
        state.todayData.loading = state.todayData.data == null;
        state.todayData.error = null;
      })
      .addCase(getTodayHeatMapStats.fulfilled, (state, action) => {
        state.todayData.loading = false;
        state.todayData.data = action.payload.data;
        state.todayData.error = null;
      })
      .addCase(getTodayHeatMapStats.rejected, (state, action) => {
        state.todayData.loading = false;
        state.todayData.error = action.payload;
        state.todayData.data = null;
      })
      // Yesterday heatmap cases
      .addCase(getYesterdayHeatMapStats.pending, (state) => {
        state.yesterdayData.loading = state.yesterdayData.data == null;
        state.yesterdayData.error = null;
      })
      .addCase(getYesterdayHeatMapStats.fulfilled, (state, action) => {
        state.yesterdayData.loading = false;
        state.yesterdayData.data = action.payload.data;
        state.yesterdayData.error = null;
      })
      .addCase(getYesterdayHeatMapStats.rejected, (state, action) => {
        state.yesterdayData.loading = false;
        state.yesterdayData.error = action.payload;
        state.yesterdayData.data = null;
      })
      // Weekly heatmap cases
      .addCase(getWeeklyHeatMapStats.pending, (state) => {
        state.weeklyData.loading = state.weeklyData.data == null;
        state.weeklyData.error = null;
      })
      .addCase(getWeeklyHeatMapStats.fulfilled, (state, action) => {
        state.weeklyData.loading = false;
        state.weeklyData.data = action.payload.data;
        state.weeklyData.error = null;
      })
      .addCase(getWeeklyHeatMapStats.rejected, (state, action) => {
        state.weeklyData.loading = false;
        state.weeklyData.error = action.payload;
        state.weeklyData.data = null;
      })
      // Monthly heatmap cases
      .addCase(getMonthlyHeatMapStats.pending, (state) => {
        state.monthlyData.loading = state.monthlyData.data == null;
        state.monthlyData.error = null;
      })
      .addCase(getMonthlyHeatMapStats.fulfilled, (state, action) => {
        state.monthlyData.loading = false;
        state.monthlyData.data = action.payload.data;
        state.monthlyData.error = null;
      })
      .addCase(getMonthlyHeatMapStats.rejected, (state, action) => {
        state.monthlyData.loading = false;
        state.monthlyData.error = action.payload;
        state.monthlyData.data = null;
      })
      // Yearly heatmap cases
      .addCase(getYearlyHeatMapStats.pending, (state) => {
        state.yearlyData.loading = state.yearlyData.data == null;
        state.yearlyData.error = null;
      })
      .addCase(getYearlyHeatMapStats.fulfilled, (state, action) => {
        state.yearlyData.loading = false;
        state.yearlyData.data = action.payload.data;
        state.yearlyData.error = null;
      })
      .addCase(getYearlyHeatMapStats.rejected, (state, action) => {
        state.yearlyData.loading = false;
        state.yearlyData.error = action.payload;
        state.yearlyData.data = null;
      });
  }
});

export const { resetHeatMapStatsState } = getHeatMapStatsSlice.actions;
export default getHeatMapStatsSlice.reducer;
